import { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  NEXT_MILESTONE_MAP,
  NEXT_REQUIRED_EVENTS_BLACK_LIST,
  TAB_ITEMS,
} from 'accounts/constants/my-home-timeline';
import { useTimeline } from 'accounts/hooks/homeowners/use-timeline';
import classNames from 'classnames/bind';
import AccountTimeline from 'components/AccountTimeline/AccountTimeline';
import { Loading } from 'components/Loading/Loading';
import TabBar from 'components/TabBar/TabBar';
import ResidentListContainer from 'containers/ResidentListContainer/ResidentListContainer';
import Space, { SPACE_TYPES } from 'corecomponents/Space/Space';
import { Text } from 'design-system';
import { flatten, isNil } from 'es-toolkit/compat';
import AccountPageSection from 'pages/Accounts/Pages/AccountPage/AccountPageSection/AccountPageSection';
// import CurrentLease from 'pages/Accounts/Pages/YourPortfolio/Properties/CurrentLease/CurrentLease';
import DisplayEvent from 'pages/Accounts/Pages/YourPortfolio/Properties/DisplayEvent/DisplayEvent';
import NextLease from 'pages/Accounts/Pages/YourPortfolio/Properties/NextLease/NextLease';
import NextRequiredEvents from 'pages/Accounts/Pages/YourPortfolio/Properties/NextRequiredEvents/NextRequiredEvents';
import { selectTimelines } from 'store/redux/homeowner-accounts/selectors';
import styles from './my-home-timeline.module.css';

const cx = classNames.bind(styles);

export function MyHomeTimeline() {
  const { selectedUnit: unit, loading } = useTimeline();
  const timelines = useSelector(selectTimelines);

  // `TAB_ITEMS` is a non-empty literal array, so its first entry is always
  // there; `noUncheckedIndexedAccess` just cannot see that.
  const [selectedTab, setSelectedTab] = useState(TAB_ITEMS[0]!);

  const { currentLease, nextLease } = unit || {};
  const {
    residents = [],
    kids = [],
    pets = [],
  } = currentLease || nextLease || {};
  const allResidents = {
    residents,
    kids,
    pets,
  };
  const allResidentsLength = [...residents, ...kids, ...pets].length;

  const timeline = timelines?.[unit?.basicInfo.unitId];

  // We need the index of the first index that is not internal to show the pulse animation.
  const firstNonInternalIndex = timeline?.timelineEvents.findIndex(
    (event: any) => event.name,
  );
  const timelineItems = [
    ...flatten(
      timeline?.timelineEvents.map((event: any, index: number) =>
        DisplayEvent(
          event,
          index === firstNonInternalIndex,
          unit?.basicInfo?.timeZone,
        ),
      ),
    ),
  ].filter((item) => !isNil(item));

  const nextRequiredEventsArray = Object.values<{ timelineEventType: string }>(
    timeline?.requiredEventsForNextMilestone || {},
  ).filter(
    (event) =>
      !(NEXT_REQUIRED_EVENTS_BLACK_LIST as Record<string, string>)[
        event.timelineEventType
      ],
  );

  // `nextMilestone` comes off the untyped timeline payload, so it cannot narrow
  // to this map's keys — the lookup is a plain string lookup either way.
  const nextMilestoneLabel = (NEXT_MILESTONE_MAP as Record<string, string>)[
    timeline?.nextMilestone
  ];

  if (loading && !timeline) {
    return (
      <div className="flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <Fragment>
      <div className={cx('flex mb-xs overflow-x-scroll', 'tabs')}>
        <TabBar
          name="unitTabBar"
          // Show only first item if no lease.
          items={currentLease || nextLease ? TAB_ITEMS : [TAB_ITEMS[0]]}
          onSelect={setSelectedTab}
          selected={selectedTab.key}
        />
      </div>
      {selectedTab.key === 'timeline' && (
        <Fragment>
          {/* If there are no events in nextRequiredEventsArray, show next milestone */}
          {(nextRequiredEventsArray.length > 0 || nextMilestoneLabel) && (
            <>
              <Text fontWeight="semibold" color="dark-gray">
                What’s next?
              </Text>
              <Space value={SPACE_TYPES.XS} />
              <NextRequiredEvents
                nextRequiredEvents={
                  nextRequiredEventsArray.length > 0
                    ? nextRequiredEventsArray
                    : [{ name: nextMilestoneLabel }]
                }
              />
              <Space value={SPACE_TYPES.SM} />
            </>
          )}
          <AccountTimeline
            className="pt-xs"
            items={timelineItems}
            includesEndTail={false}
          />
        </Fragment>
      )}
      {selectedTab.key === 'residents' && (
        <Fragment>
          {/*
            We are hidding this section from consumer.
            This will be displayed again once Deposit Free Fee and Renter Insurance costs are corrected.
          */}
          {/* <CurrentLease currentLease={currentLease} /> */}
          <NextLease nextLease={nextLease} />
          {allResidentsLength > 0 && (
            <div className="pt-[20px]">
              <AccountPageSection
                title="Belong Members"
                content={<ResidentListContainer residents={allResidents} />}
              />
            </div>
          )}
        </Fragment>
      )}
    </Fragment>
  );
}
